/**
 * Issues-tab metric helpers.
 *
 * The Store Health service exposes only per-store operations (there is no bulk/network endpoint),
 * so network-wide totals are produced by fanning out one call per store and summing the results:
 *   - getIssuesForStore  → pagination.totalResults gives the issue count for a filter
 *   - getActionItemCountForStore → actionItemCount = countIssues + countTasks (issues AND tasks)
 */
import {
    GetIssuesForStoreInput,
    GetActionItemCountForStoreInput,
} from '@amzn/coral_com-amazon-jwostorehealthservice'
import { getServiceClient } from '@api/client-factory/StoreManagementClientFactory'

// getIssuesForStore returns pagination.totalResults for the matching filter, so requesting a
// single row is enough to read the total without fetching the whole list.
const COUNT_ONLY_PAGINATION = { page: 1, pageSize: 1 }

/**
 * Sums a per-store counter across every store, fanning the calls out in parallel. A per-store
 * failure contributes 0 (and is logged) rather than rejecting the whole aggregate, so one
 * inaccessible/erroring store does not blank out the card.
 *
 * However, if ALL stores fail, the function rejects rather than returning a misleading 0.
 * This causes the card to show "—" (unavailable) instead of "0" (which implies no issues).
 *
 * Stores are processed in batches of BATCH_SIZE to limit concurrent requests.
 */
const BATCH_SIZE = 5

async function sumAcrossStores (
    storeIds: string[],
    countForStore: (storeId: string) => Promise<number>,
): Promise<number> {
    if (storeIds.length === 0) {
        return 0
    }
    let total = 0
    let failureCount = 0
    for (let i = 0; i < storeIds.length; i += BATCH_SIZE) {
        const batch = storeIds.slice(i, i + BATCH_SIZE)
        const counts = await Promise.all(
            batch.map((storeId) =>
                countForStore(storeId).catch((err) => {
                    console.warn(`issuesMetrics: count failed for store ${storeId}`, err)
                    failureCount += 1
                    return 0
                }),
            ),
        )
        total += counts.reduce((sum, n) => sum + n, 0)
    }
    // If EVERY store failed (auth expired, gateway down), reject rather than return a misleading 0
    // (which would read as "no issues"). Callers surface this as the "—" placeholder.
    if (failureCount === storeIds.length) {
        throw new Error(`issuesMetrics: all ${storeIds.length} store queries failed`)
    }
    return total
}

/** Active (unresolved) issue count for a single store. */
async function activeIssueCountForStore (storeId: string): Promise<number> {
    const res = await getServiceClient()
        .getIssuesForStore(GetIssuesForStoreInput.clone({
            storeId,
            filters: { isResolved: false },
            pagination: COUNT_ONLY_PAGINATION,
        }))
        .toPromise()
    return Number(res?.pagination?.totalResults ?? 0)
}

/** Action-required count (issues AND tasks) for a single store. */
async function actionRequiredCountForStore (storeId: string): Promise<number> {
    const res = await getServiceClient()
        .getActionItemCountForStore(GetActionItemCountForStoreInput.clone({ storeId }))
        .toPromise()
    return Number(res?.actionItemCount ?? 0)
}

/** Total ACTIVE (unresolved) issues across all supplied stores. */
export async function fetchActiveIssueCount (storeIds: string[]): Promise<number> {
    return sumAcrossStores(storeIds, activeIssueCountForStore)
}

/** Total items requiring action across BOTH issues and tasks for all supplied stores. */
export async function fetchActionRequiredCount (storeIds: string[]): Promise<number> {
    return sumAcrossStores(storeIds, actionRequiredCountForStore)
}
